import React from 'react';
import {Navigate, useParams} from 'react-router-dom';
import {Spinner} from 'react-bootstrap';
import {TabDefinition} from 'utils/types';
import {useSetContextCampaignFromURLParam} from 'utils/hooks';
import {useTypedAppSelector} from 'redux/hooks';
import {useProfile} from '../utils/useProfile';
import {effectiveCampaignPermissions} from './effectiveCampaignPermissions';

export const PathxGuard = ({
  requiredAnyPermission,
  campaignPermissions,
  campaignScoped,
  requireStaff,
  requireSuperuser,
  element,
  campaignIdParamKey,
}: {
  requiredAnyPermission: string[];
  campaignPermissions?: string[];
  campaignScoped?: boolean;
  requireStaff?: boolean;
  requireSuperuser?: boolean;
  element: React.ReactElement;
  campaignIdParamKey?: 'campaign';
}) => {
  const {hasAnyPermission, isProfileLoading, profile} = useProfile();

  const {loading: campaignLoading} = useSetContextCampaignFromURLParam(campaignIdParamKey, !!campaignPermissions);
  const contextCampaign = useTypedAppSelector(state => state.navigation.contextCampaign);

  const hasPermission = hasAnyPermission(
    requiredAnyPermission,
    effectiveCampaignPermissions(campaignScoped, campaignPermissions, contextCampaign?.permission_codes),
  );
  const hasStaffPermission = requireStaff ? profile?.is_staff : true;
  const hasSuperuserPermission = requireSuperuser ? profile?.is_superuser : true;

  if ((!profile && isProfileLoading) || campaignLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
        <Spinner animation="border" />
      </div>
    );
  }
  if (hasStaffPermission && hasSuperuserPermission && hasPermission && element) {
    return element;
  }
  return <Navigate to="/home" />;
};

export const PathxTabGuard = ({
  tabDef,
  tabParamKey,
  campaignPermissions,
  campaignScoped,
  requireStaff,
  requireSuperuser,
  element,
  campaignIdParamKey,
}: {
  tabDef: Record<string, TabDefinition>;
  tabParamKey: string;
  campaignPermissions?: string[];
  campaignScoped?: boolean;
  requireStaff?: boolean;
  requireSuperuser?: boolean;
  element: React.ReactElement;
  campaignIdParamKey?: 'campaign';
}) => {
  const {hasAnyPermission, isProfileLoading, profile} = useProfile();
  const params = useParams();
  const tabDefKey = params[tabParamKey]!;
  const tab = tabDef[tabDefKey];
  if (!tab)
    console.error(`Tab definition not found for key: ${tabDefKey}. Options are: ${Object.keys(tabDef).join(', ')}`);

  const {loading: campaignLoading} = useSetContextCampaignFromURLParam(campaignIdParamKey, !!campaignPermissions);
  const contextCampaign = useTypedAppSelector(state => state.navigation.contextCampaign);

  const hasPermission = hasAnyPermission(
    tab.requiredAnyPermission,
    effectiveCampaignPermissions(campaignScoped, campaignPermissions, contextCampaign?.permission_codes),
  );
  const hasStaffPermission = requireStaff || tab.requireStaff ? profile?.is_staff : true;
  const hasSuperuserPermission = requireSuperuser ? profile?.is_superuser : true;

  if ((!profile && isProfileLoading) || campaignLoading)
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
        <Spinner animation="border" />
      </div>
    );
  if (hasStaffPermission && hasSuperuserPermission && hasPermission && element) return element;
  if (profile) return <Navigate to="/home" />;
  return <Navigate to="/sign-in" />;
};
